import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Package, Shield, Leaf, Truck, CurrencyInr, Handshake, MagnifyingGlass, X } from '@phosphor-icons/react';

// Recursively extract plain text from a React node for search indexing
const nodeToText = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join(' ');
  if (node.props && node.props.children) return nodeToText(node.props.children);
  return '';
};

// Wrap matching substrings of a plain string with a highlight span.
// Works on strings only — React-node answers fall back to plain render.
const highlight = (text, query) => {
  if (!query || typeof text !== 'string') return text;
  const q = query.trim();
  if (!q) return text;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'));
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-[#bc64aa]/20 text-[#641b4d] px-0.5">{p}</mark>
      : p
  );
};

const categories = [
  {
    id: 'product',
    label: 'Product & Materials',
    icon: Package,
    blurb: 'What they are made of & how',
    items: [
      {
        q: 'What kind of material are you using?',
        a: 'We manufacture fibre reinforced plastic planters.'
      },
      {
        q: 'How are they made?',
        a: 'Currently they are made by hand-layup method, but we soon are going to shift to machine manufacturing.'
      },
      {
        q: 'What kind of paint do you use?',
        a: 'We use automotive paint.'
      },
      {
        q: 'What is the difference between glossy, matte, and grainy finishes?',
        a: (
          <div className="space-y-3">
            <p><span className="font-medium text-[#641b4d]">Glossy finish:</span> Smooth and reflective, with a noticeable shine that gives the product a polished, premium look.</p>
            <p><span className="font-medium text-[#641b4d]">Matte finish:</span> Non-reflective and subtle, with a soft, muted appearance that feels modern and understated.</p>
            <p><span className="font-medium text-[#641b4d]">Grainy finish:</span> A textured, multi-tone look created by layering a secondary color over a base coat, giving the surface added depth and character.</p>
          </div>
        )
      },
      {
        q: 'How is FRP different from plastic pots?',
        a: (
          <div className="space-y-3">
            <p>FRP (Fiber Reinforced Polymer) pots are engineered using a composite material, making them significantly stronger and more durable than regular plastic pots.</p>
            <ul className="space-y-2 pl-4">
              <li><span className="font-medium text-[#641b4d]">Durability:</span> FRP pots are built to last — they don't crack, warp, or become brittle easily like standard plastic.</li>
              <li><span className="font-medium text-[#641b4d]">Weather resistance:</span> They can withstand harsh sun, rain, and outdoor conditions without fading or degrading quickly.</li>
              <li><span className="font-medium text-[#641b4d]">Strength with lightweight:</span> While being lightweight like plastic, FRP offers much higher structural strength.</li>
              <li><span className="font-medium text-[#641b4d]">Premium finish:</span> FRP allows for more refined finishes and textures, giving a more modern and high-quality look compared to typical plastic pots.</li>
              <li><span className="font-medium text-[#641b4d]">Longevity:</span> Plastic pots tend to degrade over time, whereas FRP pots maintain their shape and finish for years.</li>
            </ul>
          </div>
        )
      },
      {
        q: 'Are you an importer or manufacturer?',
        a: 'We are manufacturers of FRP planters.'
      },
      {
        q: 'How frequently do you update your collection?',
        a: 'You don\'t have to wait for a whole year — we roll out new designs every 2–3 months. For updates, follow us on our social media pages.'
      }
    ]
  },
  {
    id: 'care',
    label: 'Care, Durability & Warranty',
    icon: Shield,
    blurb: 'Long-term use and looking after your pot',
    items: [
      {
        q: 'Is there any warranty on the planters?',
        a: 'Generally speaking, there is no warranty on our planters since they are FRP planters and that makes them long lasting. However, we do give a warranty on the planter\'s paint. Nothing shall happen to the quality of the colour. It will remain unfaded for at least 3 years.'
      },
      {
        q: 'Will the colour fade with direct sunlight?',
        a: 'No, colour will not fade with sunlight. Our planters are suitable for both — indoor & outdoor.'
      },
      {
        q: 'Can I put the planters in outdoor areas as well?',
        a: 'Absolutely! Our planters are weather resistant and you can decorate your outdoor spaces with them without worrying about weather-caused damage.'
      },
      {
        q: 'How do I maintain the planters\' surface?',
        a: 'It\'s very easy to keep these stylish planters clean and maintained. You can use a damp cloth followed by a dry cloth from time to time.'
      },
      {
        q: 'Is there a hole for drainage?',
        a: 'By default, our fibreglass planters come with a drainage hole. However, under special requests by you, we can make the planters without the drainage holes.'
      }
    ]
  },
  {
    id: 'plants',
    label: 'Plants & Usage',
    icon: Leaf,
    blurb: 'Growing plants & potting in your pot',
    items: [
      {
        q: 'Can we pot the plants directly into these?',
        a: 'Yes, we can plant directly into these.'
      },
      {
        q: 'Will my plant grow well in fiber planters?',
        a: 'The material doesn\'t affect the growth of the plant. But we advise that if you are going to grow a plant that attains tall heights, you should buy a big planter to give enough space to its roots to spread. Another mistake that can be avoided for long life of plants is overwatering. No matter what the material of the planter is, overwatering will always hinder plant growth.'
      },
      {
        q: 'Do you sell plants as well?',
        a: 'Nope, but we love them :)'
      }
    ]
  },
  {
    id: 'orders',
    label: 'Orders, Delivery & Replacement',
    icon: Truck,
    blurb: 'Shipping, packaging, samples and damaged goods',
    items: [
      {
        q: 'Do you do door delivery?',
        a: 'Yes. We have tied up with Delhivery, our logistics partner.'
      },
      {
        q: 'How much time will you take after placing the order?',
        a: 'This usually depends on the quantity. However, on an average if you\'re placing an order for 5–10 planters, it usually takes us 10–14 working days. Custom or bulk orders will take longer given the requirements and usually take 14–18 working days.'
      },
      {
        q: 'What kind of packaging do you use?',
        a: 'Our pots are carefully wrapped in multiple layers of protective bubble wrap and housed in durable, reinforced boxes, ensuring they reach you in perfect condition.'
      },
      {
        q: 'Is it possible to see a sample first before placing the order?',
        a: 'For bulk orders, yes.'
      },
      {
        q: 'Can I get a replacement if I receive a planter in a damaged condition?',
        a: 'Absolutely! All you have to do is take a picture of the damaged part as soon as you open the packaging and share it with us within 24 hours of delivery. We will then get back to you for the same.'
      }
    ]
  },
  {
    id: 'pricing',
    label: 'Pricing, Payment & GST',
    icon: CurrencyInr,
    blurb: 'How to pay, taxes, bulk discounts',
    items: [
      {
        q: 'How do I pay?',
        a: 'Payments can be done directly on our website or app through Razorpay.'
      },
      {
        q: 'Is there any discount on bulk orders?',
        a: 'Yes. We have discount options available. The percentage of the same depends on the quantity of your order.'
      },
      {
        q: 'What is the GST % for FRP planters?',
        a: 'It is 18%.'
      },
      {
        q: 'Is your pricing inclusive of GST?',
        a: 'No. GST will be additional 18%.'
      },
      {
        q: 'Why are your planters priced higher?',
        a: (
          <div className="space-y-3">
            <p>It depends on what you're comparing us with.</p>
            <p>Our planters are designed with a focus on material quality, durability, and a refined finish — which naturally reflects in the pricing compared to standard alternatives.</p>
            <p className="italic text-[#7a5e75]">As the Tamil saying goes, "மலிவானது நல்லதல்ல, நல்லது மலிவானதல்ல." <span className="not-italic">(Cheap isn't good, and good isn't cheap.)</span></p>
            <p>Choosing a high-quality product is an investment — one that lasts longer and delivers better value over time.</p>
          </div>
        )
      }
    ]
  },
  {
    id: 'business',
    label: 'Partner With Us',
    icon: Handshake,
    blurb: 'Becoming a distributor',
    items: [
      {
        q: 'How to become a distributor?',
        a: (
          <span>For this, it\'s better to talk. Let\'s get on a call. Here\'s our number — <a href="tel:+918754702555" className="text-[#bc64aa] underline decoration-[#bc64aa]/40 underline-offset-4 hover:decoration-[#bc64aa]">+91 87547 02555</a>.</span>
        )
      }
    ]
  }
];

const FAQ = () => {
  const [active, setActive] = useState('product');
  const [query, setQuery] = useState('');
  const activeCategory = categories.find(c => c.id === active);

  // Build searchable index once
  const searchIndex = useMemo(() => {
    const items = [];
    categories.forEach(cat => {
      cat.items.forEach((it, idx) => {
        items.push({
          catId: cat.id,
          catLabel: cat.label,
          catIcon: cat.icon,
          idx,
          question: it.q,
          answer: it.a,
          answerText: nodeToText(it.a),
        });
      });
    });
    return items;
  }, []);

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;
  const results = useMemo(() => {
    if (!isSearching) return [];
    const q = trimmedQuery.toLowerCase();
    return searchIndex.filter(item =>
      item.question.toLowerCase().includes(q) ||
      item.answerText.toLowerCase().includes(q)
    );
  }, [isSearching, trimmedQuery, searchIndex]);

  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#F4F4F4]">
      {/* Hero — dark green box matching Impact */}
      <section className="bg-[#641b4d] min-h-[70vh] flex items-center px-6 pt-24 pb-16">
        <div className="max-w-5xl mx-auto w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.3em] text-[#bc64aa] mb-8"
          >
            Frequently Asked Questions
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="faq-heading"
            className="text-4xl md:text-5xl lg:text-7xl font-light tracking-tighter text-white leading-[1.05]"
          >
            You ask, we answer.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="text-base md:text-lg text-white/70 mt-8 max-w-2xl font-light"
          >
            Everything you wanted to know about our planters — from how they're made to how they get to your doorstep.
          </motion.p>

          {/* Search bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 max-w-xl"
          >
            <div className="relative flex items-center bg-white/5 border border-white/15 focus-within:border-white/40 transition-colors">
              <MagnifyingGlass size={18} weight="light" className="absolute left-4 text-white/60" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="faq-search-input"
                placeholder="Search — e.g. warranty, outdoor, GST, delivery…"
                className="w-full bg-transparent text-white placeholder-white/40 pl-12 pr-12 py-4 text-sm outline-none"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  data-testid="faq-search-clear"
                  className="absolute right-3 p-1.5 text-white/60 hover:text-white transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category pills — hidden while searching */}
      {!isSearching && (
        <section className="border-b border-[#E5E2DC] bg-white sticky top-[72px] z-30">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex gap-2 overflow-x-auto py-4 scrollbar-none">
              {categories.map(cat => {
                const Icon = cat.icon;
                const isActive = active === cat.id;
                return (
                  <button
                    key={cat.id}
                    data-testid={`faq-cat-${cat.id}`}
                    onClick={() => setActive(cat.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider whitespace-nowrap transition-all border ${
                      isActive
                        ? 'bg-[#641b4d] text-white border-[#641b4d]'
                        : 'bg-white text-[#7a5e75] border-[#E5E2DC] hover:border-[#641b4d] hover:text-[#641b4d]'
                    }`}
                  >
                    <Icon size={14} weight={isActive ? 'fill' : 'regular'} />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Results view (searching) OR category view */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto">
          {isSearching ? (
            <motion.div
              key={`search-${trimmedQuery}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center justify-between mb-10 flex-wrap gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-[#bc64aa] mb-2">Search Results</p>
                  <h2 data-testid="faq-search-count" className="text-2xl md:text-3xl font-light text-[#641b4d] tracking-tight">
                    {results.length} {results.length === 1 ? 'answer' : 'answers'} for "{trimmedQuery}"
                  </h2>
                </div>
                {results.length > 0 && (
                  <button
                    onClick={() => setQuery('')}
                    className="text-xs uppercase tracking-wider text-[#7a5e75] hover:text-[#641b4d] underline underline-offset-4"
                  >
                    Browse all categories
                  </button>
                )}
              </div>

              {results.length === 0 ? (
                <div data-testid="faq-search-empty" className="bg-white border border-[#E5E2DC] p-10 md:p-14 text-center">
                  <p className="text-sm text-[#7a5e75] mb-6">
                    We couldn't find anything matching "{trimmedQuery}".
                  </p>
                  <p className="text-sm text-[#7a5e75] mb-8">
                    Try a different keyword, or reach out to us directly.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Link
                      to="/custom-inquiry"
                      className="px-6 py-3 bg-[#641b4d] text-white text-xs uppercase tracking-wider hover:bg-[#4a1339] transition-colors"
                    >
                      Ask us directly
                    </Link>
                    <button
                      onClick={() => setQuery('')}
                      className="px-6 py-3 border border-[#641b4d] text-[#641b4d] text-xs uppercase tracking-wider hover:bg-[#641b4d] hover:text-white transition-colors"
                    >
                      Clear search
                    </button>
                  </div>
                </div>
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {results.map((item, i) => {
                    const Icon = item.catIcon;
                    return (
                      <AccordionItem
                        key={`${item.catId}-${item.idx}`}
                        value={`res-${i}`}
                        data-testid={`faq-search-result-${i}`}
                        className="border-b border-[#E5E2DC]"
                      >
                        <AccordionTrigger className="hover:no-underline hover:text-[#bc64aa] py-6 text-left">
                          <div className="flex-1 pr-4">
                            <div className="flex items-center gap-2 mb-1.5">
                              <Icon size={12} className="text-[#bc64aa]" />
                              <span className="text-[10px] uppercase tracking-[0.2em] text-[#7a5e75]">{item.catLabel}</span>
                            </div>
                            <p className="text-base md:text-lg font-light text-[#641b4d]">
                              {highlight(item.question, trimmedQuery)}
                            </p>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm md:text-base text-[#7a5e75] leading-relaxed pb-6 pr-4">
                          {typeof item.answer === 'string'
                            ? <p>{highlight(item.answer, trimmedQuery)}</p>
                            : item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </motion.div>
          ) : (
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-2">
                {activeCategory.icon && <activeCategory.icon size={18} weight="fill" className="text-[#bc64aa]" />}
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#bc64aa]">{activeCategory.label}</p>
              </div>
              <h2 className="text-2xl md:text-3xl font-light text-[#641b4d] tracking-tight mb-12 max-w-2xl">
                {activeCategory.blurb}
              </h2>

              <Accordion type="single" collapsible className="w-full">
                {activeCategory.items.map((item, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    data-testid={`faq-item-${active}-${i}`}
                    className="border-b border-[#E5E2DC]"
                  >
                    <AccordionTrigger className="text-base md:text-lg font-light text-[#641b4d] hover:no-underline hover:text-[#bc64aa] py-6 text-left">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-[#7a5e75] leading-relaxed pb-6 pr-4">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          )}
        </div>
      </section>

      {/* Still have questions CTA — hidden in empty-results state since a CTA is already shown there */}
      {!(isSearching && results.length === 0) && (
        <section className="px-6 pb-24">
          <div className="max-w-4xl mx-auto bg-white border border-[#E5E2DC] p-10 md:p-14 text-center">
            <p className="text-xs uppercase tracking-[0.25em] text-[#bc64aa] mb-4">Still curious?</p>
            <h3 className="text-2xl md:text-4xl font-light text-[#641b4d] tracking-tight mb-6">
              Can't find what you're looking for?
            </h3>
            <p className="text-[#7a5e75] mb-8 max-w-xl mx-auto">
              Talk to us directly — whether it's about a custom design, a bulk order, or just a specific question about your space.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link
                to="/custom-inquiry"
                data-testid="faq-cta-custom"
                className="px-6 py-3 bg-[#641b4d] text-white text-xs uppercase tracking-wider hover:bg-[#4a1339] transition-colors"
              >
                Start a Custom Order
              </Link>
              <a
                href="tel:+918754702555"
                data-testid="faq-cta-call"
                className="px-6 py-3 border border-[#641b4d] text-[#641b4d] text-xs uppercase tracking-wider hover:bg-[#641b4d] hover:text-white transition-colors"
              >
                Call +91 87547 02555
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default FAQ;
